import React, { useState, useEffect } from "react"
import { Route } from "react-router-dom"
import { withAuthenticationRequired } from "@auth0/auth0-react"

import SnackBar from "./SnackBar"
import { getUserStoresBasedonRoles } from "./utils"
import { getCurrentUserRole, setStoreId } from "../utils/api"
import { useSession } from "../contexts/SessionContext"
import { checkAuthorization } from "../utils/permissions"

const renderMergedProps = (component, ...rest) => {
  const finalProps = Object.assign({}, ...rest)
  return React.createElement(component, finalProps)
}

/**
 * `permission` is optional and, when passed, grants access on top of `roles` (either
 * one satisfies the route - a user should have either a matching role or the permission).
 */
const PrivateRoute = ({ component, roles, permission, ...rest }) => {
  const [userStores, setUserStores] = useState(null)
  const [hasStoreAccess, setHasStoreAccess] = useState(null)
  const session = useSession()

  // Check if the user has access to the store
  const checkAccess = async (storeId) => {
    const hasAccess = userStores.includes("All") || userStores.includes(storeId)
    if (!hasAccess && storeId) {
      // If not in user stores, check the platform DB
      // TODO: Transition completely to using the roles database to eliminate dependence on the platform database
      const resp = await getCurrentUserRole(storeId)
      return roles.includes(resp.role)
    }
    return hasAccess
  }

  useEffect(() => {
    const fetchUserStores = async () => {
      if (Array.isArray(roles) && roles.length > 0) {
        const userStores = await getUserStoresBasedonRoles(roles)
        setUserStores(userStores)
      } else {
        setUserStores([])
      }
    }
    fetchUserStores()
  }, [roles])

  useEffect(() => {
    const checkStoreAccess = async () => {
      const storeId = rest?.computedMatch?.params?.storeId
      if (storeId) {
        setStoreId(storeId)
      }
      const access = await checkAccess(storeId)
      setHasStoreAccess(access)
    }
    if (userStores !== null) {
      checkStoreAccess()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userStores])

  const storeId = rest?.computedMatch?.params?.storeId
  const { authorized: hasPermissionAccess, isLoading: isPermissionsLoading } = permission
    ? checkAuthorization(session, permission, storeId)
    : { authorized: false, isLoading: false }

  if (hasStoreAccess === null || isPermissionsLoading) {
    return <div>Loading...</div> // Show loading while checking access
  }

  const hasAccess = hasStoreAccess || hasPermissionAccess

  return (
    <Route
      {...rest}
      render={(routeProps) => {
        if (hasAccess) {
          // If user has access, render the component
          return renderMergedProps(component, routeProps, rest)
        } else {
          // If user doesn't have access, display unauthorized user)
          return <SnackBar msgBGColor="error" userMessage="Unauthorized user" />
        }
      }}
    />
  )
}
/**
 * Auth0Provider will redirect back to this private route if the user hasn't
 * been authenticated yet.
 */
export default withAuthenticationRequired(PrivateRoute, {
  // Show a message while the user waits to be redirected to the login page.
  onRedirecting: () => <div>Redirecting you to the login page...</div>,
})
